import React from "react";
import type { Theme } from "@material-ui/core";
import { Box, Chip, createStyles, makeStyles } from "@material-ui/core";

import { CloseOutlined as CloseOutlinedIcon } from "@eroad/icons/material";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: "flex",
      justifyContent: "left",
      flexWrap: "wrap",
      listStyle: "none",
      padding: theme.spacing(0.5),
      margin: 0,
      "& li": {
        padding: "2px",
      },
    },
    chip: {
      height: "21px",
      margin: theme.spacing(0.5),
      backgroundColor: theme.palette.secondary.light,
      fontWeight: 700,
    },
    chipField: {
      color: theme.palette.secondary.dark,
      fontWeight: 400,
      marginRight: "0.5em",
    },
    chipValue: {
      color: theme.palette.primary.dark,
    },
  }),
);

export interface QuickFilter {
  key: string;
  value: string;
}

export interface QuickFieldFilter extends QuickFilter {
  field: string;
}
export interface QuickIconFilter extends QuickFilter {
  icon: React.ReactNode;
}

export interface QuickFilterProps {
  filters: QuickFilter[];
  allowDelete?: boolean;
  onDelete: (filter: QuickFilter) => void;
}

const isQuickFieldFilter: (filter: unknown) => boolean = (filter: unknown): filter is QuickFieldFilter =>
  (filter as QuickFieldFilter).field !== undefined;

const isQuickIconFilter: (filter: unknown) => boolean = (filter: unknown): filter is QuickIconFilter =>
  (filter as QuickIconFilter).icon !== undefined;

const QuickFilters = ({ filters, allowDelete, onDelete }: QuickFilterProps): JSX.Element => {
  const classes = useStyles();
  const [chipFilters, setChipFilters] = React.useState<QuickFilter[]>(filters);
  React.useEffect(() => setChipFilters(filters), [filters]);

  const handleDelete = (filter: QuickFilter) => {
    setChipFilters(chipFilters.filter((item) => item.value !== filter.value));
    onDelete(filter);
  };

  const fieldLabel = (filter: QuickFieldFilter) => (
    <>
      <span className={classes.chipField}>{filter.field}</span>
      <span className={classes.chipValue}>{filter.value}</span>
    </>
  );

  return (
    <Box component="ul" className={classes.root}>
      {chipFilters.map((filter: QuickFilter) => {
        const testId = filter?.value ? `${filter?.value?.replace(/\s+/g, "-").toLowerCase()}-filter` : "";
        return (
          <li key={`${isQuickFieldFilter(filter) ? `${(filter as QuickFieldFilter).field}-` : ""}${filter.value}`}>
            <Chip
              data-testid={testId}
              label={isQuickFieldFilter(filter) ? fieldLabel(filter as QuickFieldFilter) : filter.value}
              onDelete={() => handleDelete(filter)}
              icon={isQuickIconFilter(filter) ? ((filter as QuickIconFilter).icon as React.ReactElement) : undefined}
              deleteIcon={allowDelete ? <CloseOutlinedIcon style={{ fontSize: 17 }} /> : <></>}
              className={classes.chip}
            />
          </li>
        );
      })}
    </Box>
  );
};

export default QuickFilters;
